import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Select, ConfigProvider } from 'antd';
import type { TableColumnsType } from 'antd';
import { SearchIcon } from '../../assets/icons';
import { PLATFORM_TABLE_THEME } from '../admin/platform/tableTheme';
import {
  useClientRoles,
  deleteRole,
  typeOf,
  scopeLine,
  typeChip,
  permCount,
  LEVELS,
  type Role,
} from './clientRolesStore';

/* ------------------------------------------------------------------ *
 * Client Admin — Roles list (listing). Search + filter dropdowns row
 * (same pattern as the Users table) above the app table. "New role"
 * opens the add page; the role name links to the add page prefilled.
 * ------------------------------------------------------------------ */

const FILTER_CONTROL_HEIGHT = 38;

export default function ClientRolesPage() {
  const navigate = useNavigate();
  const roles = useClientRoles();
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'Functional' | 'Territorial'>('all');
  const [scopeFilter, setScopeFilter] = useState<string>('all');

  const query = q.trim().toLowerCase();
  const data = roles.filter((r) => {
    if (query && r.name.toLowerCase().indexOf(query) < 0) return false;
    if (typeFilter !== 'all' && typeOf(r.terr) !== typeFilter) return false;
    if (scopeFilter === 'org' && r.terr !== 'org') return false;
    if (scopeFilter !== 'all' && scopeFilter !== 'org' && !(r.terr === 'assigned' && r.level === scopeFilter)) return false;
    return true;
  });

  const columns: TableColumnsType<Role> = [
    {
      title: 'Role',
      key: 'name',
      render: (_, r) => (
        <button
          type="button"
          onClick={() => navigate(`/client/roles/new?from=${r.id}`)}
          title="Use as a template for a new role"
          className="text-left text-sm font-semibold text-ink hover:text-brand hover:underline"
        >
          {r.name}
        </button>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      width: 150,
      render: (_, r) => {
        const type = typeOf(r.terr);
        return (
          <span className={['rounded-full px-2.5 py-0.5 text-xs font-semibold', typeChip(type)].join(' ')}>{type}</span>
        );
      },
    },
    {
      title: 'Scope',
      key: 'scope',
      width: 180,
      render: (_, r) => <span className="text-[13px] text-ink-secondary">{scopeLine(r)}</span>,
    },
    {
      title: 'Users',
      key: 'users',
      width: 100,
      align: 'right',
      sorter: (a, b) => a.users - b.users,
      render: (_, r) => <span className="text-[13px] font-semibold text-ink">{r.users}</span>,
    },
    {
      title: 'Permissions',
      key: 'perms',
      width: 130,
      align: 'right',
      sorter: (a, b) => permCount(a.perms) - permCount(b.perms),
      render: (_, r) => <span className="text-[13px] text-ink-secondary">{permCount(r.perms)} allowed</span>,
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      align: 'right',
      render: (_, r) => (
        <button
          type="button"
          onClick={() => deleteRole(r.id)}
          aria-label={`Delete ${r.name}`}
          title="Delete role"
          className="rounded-md border border-[#fecaca] bg-white px-2.5 py-1 text-[11px] font-semibold text-[#dc2626] transition-colors hover:bg-[#fef2f2]"
        >
          Delete
        </button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-[18px]">
      <section className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-brand">CLIENT SETUP</span>
          <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">Roles &amp; permissions</h1>
          <p className="max-w-[820px] text-sm leading-6 text-ink-secondary">
            A role decides what someone can do in each module. Its scope — organisation-wide, or a territory level —
            decides whose work reaches them, and sets the role type.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/client/roles/new')}
          className="h-11 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark"
        >
          + New role
        </button>
      </section>

      <section className="flex flex-col gap-3 rounded-xl border border-line/30 bg-white p-5 shadow-card">
        {/* Search + filters — a single row (same pattern as the Users table). */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-0 flex-1">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#64748b]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search roles by name..."
              className="w-full rounded-lg border border-[#e2e8f0] bg-white py-2.5 pl-9 pr-4 text-sm text-ink placeholder:text-[#46556f] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </div>
          <div className="w-[180px] shrink-0">
            <Select
              className="w-full"
              style={{ height: FILTER_CONTROL_HEIGHT }}
              value={typeFilter}
              onChange={setTypeFilter}
              options={[
                { value: 'all', label: 'All types' },
                { value: 'Functional', label: 'Functional' },
                { value: 'Territorial', label: 'Territorial' },
              ]}
            />
          </div>
          <div className="w-[200px] shrink-0">
            <Select
              className="w-full"
              style={{ height: FILTER_CONTROL_HEIGHT }}
              value={scopeFilter}
              onChange={setScopeFilter}
              options={[
                { value: 'all', label: 'All scopes' },
                { value: 'org', label: 'Org-wide' },
                ...LEVELS.map((l) => ({ value: l, label: `Per ${l}` })),
              ]}
            />
          </div>
        </div>

        <ConfigProvider theme={PLATFORM_TABLE_THEME}>
          <Table<Role>
            className="chunaav-table"
            columns={columns}
            dataSource={data}
            rowKey="id"
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No roles match.' }}
          />
        </ConfigProvider>
      </section>
    </div>
  );
}
